import { useEffect, useState } from "react";
import { Loader2, Send, Landmark, Paperclip, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";

const ta = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40";

/** Inner form: remounts fresh each time the dialog opens (DialogContent only
 *  mounts while open), so prefill loads cleanly with no reset effect. */
function AcsaForm({ invoice, onClose, onDone }) {
  const [pre, setPre] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${invoice.id}/acsa-email`)
      .then((r) => {
        const p = r.data;
        setPre(p);
        setTo(p.to || "");
        setCc(p.cc || "");
        setSubject(p.subject || "");
        setBody(p.body || "");
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/invoices/${invoice.id}/send-acsa`, {
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject,
        body,
      });
      setResult(r.data);
      onDone?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-3 py-2" data-testid="acsa-send-success">
        <div className="flex items-start gap-2 rounded-md border border-status-paid/30 bg-status-paid/10 px-3 py-2.5 text-sm text-foreground">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-status-paid" />
          <div>
            <p className="font-medium">Sent to ACSA — invoice {result.number}</p>
            <p className="text-xs text-muted-foreground">
              To {(result.to || []).join(", ")}{result.cc?.length ? ` · cc ${result.cc.join(", ")}` : ""} · Resend id {result.id}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="acsa-done-button">Done</Button>
        </DialogFooter>
      </div>
    );
  }

  if (!pre && !error) {
    return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-1 pr-1">
        <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="acsa-to">To</Label>
          <Input id="acsa-to" data-testid="acsa-to-input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="jgodfrey@acsa.org" />
        </div>
        <div>
          <Label htmlFor="acsa-cc">CC</Label>
          <Input id="acsa-cc" data-testid="acsa-cc-input" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="linda@firm.com" />
        </div>
      </div>
      <div>
        <Label htmlFor="acsa-subject">Subject</Label>
        <Input id="acsa-subject" data-testid="acsa-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="acsa-body">Message</Label>
        <textarea id="acsa-body" data-testid="acsa-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={9} className={ta} />
      </div>

      <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm" data-testid="acsa-attachment-note">
        <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-foreground">Attached: <span className="font-medium">{pre?.attachment_filename}</span></span>
        <span className="ml-auto text-xs text-muted-foreground">the matter&apos;s frozen ACSA invoice PDF</span>
      </div>

      <div className="rounded-md border border-border bg-surface p-3 text-sm" data-testid="acsa-email-preview">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Email preview</p>
        <dl className="space-y-1 text-xs">
          <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">From</dt><dd className="text-foreground">{pre?.from}</dd></div>
          <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">Reply-to</dt><dd className="text-foreground">{pre?.reply_to}</dd></div>
          <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">To</dt><dd className="text-foreground">{to || "—"}</dd></div>
          {cc.trim() && <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">CC</dt><dd className="text-foreground">{cc}</dd></div>}
          <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">Subject</dt><dd className="font-medium text-foreground">{subject || "—"}</dd></div>
        </dl>
        <pre className="mt-2 whitespace-pre-wrap border-t border-border pt-2 font-sans text-sm text-foreground/90">{body}</pre>
      </div>

      {error && <p className="text-sm text-destructive" data-testid="acsa-send-error">{error}</p>}
      </div>

      <DialogFooter className="shrink-0 border-t border-border pt-3">
        <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="acsa-cancel-button">Cancel</Button>
        <Button onClick={send} disabled={busy || !to.trim()} data-testid="acsa-send-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to ACSA
        </Button>
      </DialogFooter>
    </div>
  );
}

export function AcsaSendDialog({ open, onOpenChange, invoice, onDone }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg flex max-h-[90vh] flex-col" data-testid="acsa-send-dialog">
        <DialogTitle className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-status-paid/10 text-status-paid"><Landmark className="h-4 w-4" /></span>
          Send to ACSA
        </DialogTitle>
        <DialogDescription>
          Email the funder the frozen ACSA invoice {invoice?.number} as a PDF attachment. Review everything below — nothing is sent until you click Send.
        </DialogDescription>
        <AcsaForm invoice={invoice} onClose={() => onOpenChange(false)} onDone={onDone} />
      </DialogContent>
    </Dialog>
  );
}
